import React from 'react';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { InstallProductService } from '../../pdp/installProductService/InstallProductService';
import ProductService from '../../pdp/productService/ProductService';
import { ServiceCategory, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { styles } from './CartService.css';
import { Facets } from '../../pdp/installProductService/types';
import { IdPrefixProvider } from '@jbhi-fi/components/src/IdPrefix';
import { useIdPrefixAttacher } from '@jbhi-fi/components/src/IdPrefix/context/IdPrefixContext';

export type ServicesModuleConfig = {
  variant: 'cart' | 'minicart';
  lineId: string;
  productSku: string;
  productServiceLink?: string;
  gtmData?: any;
  primaryServices?: Map<ServiceCategory, ServicesProps>;
  onAddService?: (productSku: string, serviceId: string, offeringId: string) => Promise<void>;
  trackingMessage?: string;
  preSelectedOfferingId?: string;
  facets?: Facets;
};

export type PrimaryServiceModuleConfig = ServicesModuleConfig & { primaryService: ServicesProps | undefined };

const InstallModule = ({
  variant,
  lineId,
  productSku,
  productServiceLink,
  primaryService,
  preSelectedOfferingId,
  facets,
}: PrimaryServiceModuleConfig) => {
  const attachIdPrefix = useIdPrefixAttacher();
  return primaryService ? (
    <InstallProductService
      key={attachIdPrefix(lineId)}
      productSku={productSku}
      productLineId={lineId}
      productServiceLink={productServiceLink}
      service={primaryService}
      preSelectedOfferingId={preSelectedOfferingId}
      variant={variant}
      facets={facets}
    />
  ) : null;
};

export const renderInstallModule = (props: PrimaryServiceModuleConfig) => {
  return (
    <IdPrefixProvider prefix="install">
      <InstallModule {...props} />
    </IdPrefixProvider>
  );
};

export const renderServicesModule = ({
  lineId,
  productSku,
  primaryService,
  onAddService,
  gtmData,
  trackingMessage,
}: PrimaryServiceModuleConfig) => {
  if (!onAddService) return null;

  return primaryService ? (
    <ProductService
      key={`service-${lineId}`}
      productSku={productSku}
      service={primaryService}
      onAdd={onAddService}
      gtmData={gtmData}
      trackingMessage={trackingMessage}
    />
  ) : null;
};

export interface ServicesCarouselProps extends ServicesModuleConfig {
  slidesPerView?: number;
  isDesktop?: boolean;
}

export const ServicesCarousel = (props: ServicesCarouselProps) => {
  const { slidesPerView, isDesktop = false } = props;

  const { primaryServices } = props;

  if (!primaryServices?.size) return null;

  const installProps: PrimaryServiceModuleConfig = { ...props, primaryService: primaryServices.get('install') };
  const nonInstallProps: PrimaryServiceModuleConfig = { ...props, primaryService: primaryServices.get('nonInstall') };

  const installsModule = installProps.primaryService ? renderInstallModule(installProps) : null;
  const servicesModule = nonInstallProps.primaryService ? renderServicesModule(nonInstallProps) : null;

  const slides = [installsModule, servicesModule].filter(Boolean);
  if (slides.length === 0) return null;

  return isDesktop || slides.length === 1 ? (
    <div className={styles.servicesContainer}>{slides}</div>
  ) : (
    <Carousel showPagination={false} slidesPerView={slidesPerView} showProgressBar={false} showArrows={false} fullBleed>
      {slides}
    </Carousel>
  );
};
